import "./settingsPageStyles.scss";

import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Button, CardBody, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useCopyToClipboard } from "usehooks-ts";

import { CustomInput } from "../../../../components/CustomInput/CustomInput";
import {
  CheckIcon,
  CopyIcon,
  ExclamationIcon,
  RefreshIcon,
  StopIcon,
} from "../../../../components/icons";
import { useOrganizationContext } from "../../../../contexts/organizations/organizationsContext";
import { useUpdateOrganization } from "../../../../hooks/reactQueryHooks/useOrganization";
import { DeleteOrganizationModal } from "../DeleteOrganizationModal/DeleteOrganizationModal";
// import { PrivateLinkSwitcher } from "../PrivateLinkSwitcher/PrivateLinkSwitcher";

const statusesMap: Record<
  string,
  {
    label: string;
    icon: JSX.Element;
  }
> = {
  Subscribed: {
    label: "Subscribed",
    icon: <CheckIcon size={16} />,
  },
  Suspended: {
    label: "Suspended",
    icon: <ExclamationIcon size={16} />,
  },
  Unsubscribed: {
    label: "Unsubscribed",
    icon: <ExclamationIcon size={16} />,
  },
  PendingCheckout: {
    label: "Pending checkout",
    icon: <RefreshIcon size={16} />,
  },
  Blocked: {
    label: "Blocked",
    icon: <StopIcon size={16} />,
  },
};

const SettingsPage: React.FC = () => {
  const { currentOrganization, updateOrganization } = useOrganizationContext();
  const [orgName, setOrgName] = useState(currentOrganization?.name || "");
  //   const [image, setImage] = useState<File | null>(null);
  //   const [preview, setPreview] = useState<string | null>(null);
  //   const [fileName, setFileName] = useState<string>('');
  // const [showModal, setShowModal] = useState<boolean>(false);
  // const [settingsInProcess, setSettingsInProcess] = useState<boolean>(false);
  const [_, copy] = useCopyToClipboard();

  useEffect(() => {
    setOrgName(currentOrganization?.name || "");
  }, [currentOrganization]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrgName(e.target.value);
  };

  //   const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //     const file = e.target.files?.[0];

  //     if (!file) {
  //       setPreview(null);
  //       setFileName('');
  //       setImage(null);
  //       return;
  //     }

  //     if (file && file.type.startsWith('image/')) {
  //       const reader = new FileReader();
  //       reader.onloadend = () => {
  //         setImage(file);
  //         setPreview(reader.result as string);
  //         setFileName(file.name);
  //       };
  //       reader.readAsDataURL(file);
  //     } else {
  //       alert('Please choose an image file.');
  //     }
  //   };

  const isDisabled = orgName === currentOrganization?.name || !orgName.trim(); // && !image;

  const mutation = useUpdateOrganization();

  const handleSave = async () => {
    if (isDisabled) return;

    if (currentOrganization) {
      const res = await mutation.mutateAsync({
        name: orgName,
        organizationId: currentOrganization?.id || "",
        isPublicBalancerEnabled:
          currentOrganization?.isPublicBalancerEnabled || false,
      });
      updateOrganization({
        ...currentOrganization,
        name: res.name,
      });
    }

    // Send the image to the server here
    // console.log(image);
  };

  // const handleClose = () => {
  //   setShowModal(false);
  // };

  // const handleDelete = () => {
  //   setShowModal(false);
  //   setOrgName("");
  // };

  const { created = 0 } = currentOrganization || {};

  const createedNum = Number(created);

  const copyToClipboard = (text: string) => {
    copy(text)
      .then(() => {
        toast.success("Copied to clipboard!");
      })
      .catch(console.error);
  };

  const createdDate = useMemo(() => {
    return createedNum > 0
      ? moment(createedNum * 1000)
          .local()
          .format("MMM D, YYYY")
      : "";
  }, [createedNum]);

  const status = currentOrganization?.subscriptionInfo?.status;

  return (
    <div className="card settings">
      <div className="card-header pt-9 border-0 align-items-center ">
        <h2 className="fw-bold mb-3 settings__title">
          {currentOrganization?.name || ""}
        </h2>
        <div className="w-100">
          <table className="settings__info">
            <tbody>
              <tr>
                <td>Created date:</td>
                <td>
                  <div className="d-flex align-items-center">
                    {createdDate}
                    <Button
                      variant="link"
                      onClick={() => {
                        copyToClipboard(createdDate);
                      }}
                      className="copy-btn"
                    >
                      <CopyIcon className="ms-2" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
              <tr>
                <td>Organization ID:</td>
                <td>
                  <div className="d-flex align-items-center">
                    {currentOrganization?.id && (
                      <>
                        {currentOrganization?.id}
                        <Button
                          variant="link"
                          onClick={() => {
                            copyToClipboard(currentOrganization?.id);
                          }}
                          className="copy-btn"
                        >
                          <CopyIcon className="ms-2" size={16} />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
              <tr>
                <td>Subscription ID:</td>
                <td>
                  <div className="d-flex align-items-center">
                    {currentOrganization?.subscriptionInfo?.subscriptionId ? (
                      <>
                        {currentOrganization?.subscriptionInfo?.subscriptionId}
                        <Button
                          variant="link"
                          onClick={() => {
                            copyToClipboard(
                              currentOrganization?.subscriptionInfo
                                ?.subscriptionId || "",
                            );
                          }}
                          className="copy-btn"
                        >
                          <CopyIcon className="ms-2" size={16} />
                        </Button>
                      </>
                    ) : (
                      <Spinner
                        className="ms-2"
                        size="sm"
                        animation="border"
                        data-testid="spinner"
                      />
                    )}
                  </div>
                </td>
              </tr>
              <tr>
                <td>Subscription status:</td>
                <td>
                  {status ? (
                    <div className="d-flex align-items-center">
                      {status === "Subscribed" ? (
                        <>
                          {statusesMap[status]?.label}
                          <span className="d-flex status-icon subscribed">
                            {statusesMap[status]?.icon}
                          </span>
                        </>
                      ) : (
                        <>
                          {statusesMap[status]?.label}
                          <span className="d-flex status-icon">
                            {statusesMap[status]?.icon}
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <Spinner
                      className="ms-2"
                      size="sm"
                      animation="border"
                      data-testid="spinner"
                    />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {/* <div className=" d-flex">
          <Button
            onClick={openModal}
            className="flex-shrink-0 ms-md-auto"
          >
            Delete organization
          </Button>
        </div> */}

      <CardBody>
        <hr className="my-0 mb-10" />
        <div className="flex-shrink-1">
          <p className="fw-bold">
            Edit the {currentOrganization?.name || ""} organization:
          </p>
          <div className="d-flex flex-column position-relative my-1">
            <div className="settings__input-wrapper">
              <CustomInput
                inputValue={orgName}
                placeholder="Organization name"
                label="Name:"
                name="orgName"
                autoFocus={true}
                type="text"
                handleChange={handleChange}
                className="settings__input"
                maxLength={150}
              />
            </div>
          </div>
          {/* <PrivateLinkSwitcher
            currentOrganization={currentOrganization}
            setSettingsInProcess={setSettingsInProcess}
            isMutating={mutation.isLoading}
          /> */}
          {/* <p className="my-3 mt-5 fs-7">Logo:</p>
          <label className="cursor-pointer mb-5">
            <input
              hidden
              type="file"
              onChange={handleImageChange}
            />
            {preview ? (
              <>
                <div className="settings__logo--preview settings__logo h-auto">
                  <img
                    src={preview}
                    alt="Preview"
                    className="img-fluid"
                  />
                </div>
                {fileName && <p className="mt-3">{fileName}</p>}
              </>
            ) : (
              <>
                <div className="settings__logo d-flex justify-content-center align-items-center rounded-3">
                  No image chosen
                </div>
              </>
            )}
          </label> */}
          <Button
            // disabled={isDisabled || mutation.isLoading || settingsInProcess}
            disabled={isDisabled || mutation.isLoading}
            className="d-block mt-9"
            onClick={handleSave}
            variant="primary"
          >
            Save
            {mutation.isLoading && (
              <Spinner
                className="ms-2"
                size="sm"
                animation="border"
                data-testid="spinner"
              />
            )}
          </Button>
        </div>
      </CardBody>
      {/* <DeleteOrganizationModal
        name={orgName}
        onDelete={handleDelete}
        show={showModal}
        handleClose={handleClose}
      /> */}
    </div>
  );
};
export { SettingsPage };
